import { InfoCircleOutlined, TranslationOutlined } from '@ant-design/icons'
import Program from '@components/membership/program/Program'
import { MembershipProfile as MembershipProfileModel } from '@models/membership/MembershipProfile'
import { useAppSelector } from '@store/hooks'
import { selectMembershipProfileById } from '@store/membership/MembershipProfileReducer'
import { selectSubscriptionById } from '@store/membership/SubscriptionReducer'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { ConditionalWrapper } from '@supplement-bacon/react-toolkit/utils'
import { Avatar, AvatarProps, Flex, Modal, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { FC, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

const { Title, Text } = Typography

type LinkType = 'membership-profile' | 'app-user'
type Direction = 'horizontal' | 'vertical'

type Props = SBModelComponent<MembershipProfileModel> & {
  /** If true, the avatar won't be clickable/linkable */
  noLinkToDetails?: boolean
  /** Size of the avatar */
  size?: AvatarProps['size']
  /** Layout direction for the avatar and text */
  direction?: Direction
  /** Type of link to generate when clicking */
  link?: LinkType
  /** Title level when size is not 'default' */
  level?: 1 | 2 | 3 | 4 | 5
  /** If true, clicking on the picture opens a modal with the full-size image */
  pictureOpensInModal?: boolean
  /** If true, shows additional attributes (email, phone, locale, birth date) */
  showAttributes?: boolean
}

const MembershipProfileAvatar: FC<Props> = ({
  id,
  model,
  noLinkToDetails = false,
  link = 'membership-profile',
  size = 'default',
  level = 4,
  pictureOpensInModal = false,
  showAttributes = false,
  direction = 'horizontal',
}) => {
  const item = model ? model : useAppSelector(selectMembershipProfileById(id!))

  const subscription = useAppSelector(
    selectSubscriptionById((item?.subscription as number) ?? -1)
  )
  const program = subscription?.plan?.program

  const [fullAvatarModalIsOpen, setFullAvatarModalIsOpen] = useState(false)

  // Memoize computed values
  const initials = useMemo(() => {
    if (item?.profilePictureUrl) return null
    const first = item?.firstName?.charAt(0) || ''
    const last = item?.lastName?.charAt(0) || ''
    return first || last ? `${first}${last}`.toUpperCase() : '--'
  }, [item?.firstName, item?.lastName, item?.profilePictureUrl])

  const fullName = useMemo(
    () => `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
    [item?.firstName, item?.lastName]
  )

  const linkPath = useMemo(() => {
    if (!item) return ''
    return link === 'membership-profile'
      ? `/membership/members/${item.id}`
      : `/users/${item.appUser}/membership`
  }, [item, link])

  const avatarSize = useMemo(() => (size === 'large' ? 60 : size), [size])

  const age = useMemo(() => {
    if (!item?.birthDate) return null
    return dayjs().diff(dayjs(item.birthDate), 'year')
  }, [item?.birthDate])

  const formattedBirthDate = useMemo(() => {
    if (!item?.birthDate) return null
    return dayjs(item.birthDate).format('DD/MM/YYYY')
  }, [item?.birthDate])

  // Early return if no data
  if (!item) {
    return null
  }

  const handleAvatarClick = () => {
    if (pictureOpensInModal && item.profilePictureUrl) {
      setFullAvatarModalIsOpen(true)
    }
  }

  const handleModalClose = () => setFullAvatarModalIsOpen(false)

  const renderAvatar = () => (
    <Avatar
      onClick={handleAvatarClick}
      src={item.profilePictureUrl}
      size={avatarSize}
      style={{
        color: '#fff',
        backgroundColor: '#8e978a',
        fontSize: size === 'large' ? 25 : undefined,
        border: 0,
        cursor:
          pictureOpensInModal && item.profilePictureUrl ? 'pointer' : 'default',
      }}
      aria-label={`Avatar de ${fullName}`}
    >
      {initials && (
        <span style={{ textTransform: 'uppercase' }}>{initials}</span>
      )}
    </Avatar>
  )

  const renderName = () => {
    const nameElement =
      size === 'default' ? (
        <Text>{fullName}</Text>
      ) : (
        <Title level={level} className="m-0">
          {fullName}
        </Title>
      )

    if (!program) return nameElement

    return (
      <Flex
        vertical
        align={direction === 'vertical' ? 'center' : 'flex-start'}
        gap={2}
      >
        {nameElement}
        <Program.Tag model={program} variant="purple" />
      </Flex>
    )
  }

  const renderAttributes = () => {
    if (!showAttributes) return null

    return (
      <Flex
        vertical={direction === 'vertical'}
        align="center"
        gap={direction === 'vertical' ? 0 : 20}
      >
        {item.email && (
          <Text type="secondary" copyable>
            {item.email}
          </Text>
        )}
        {item.phone && (
          <Text type="secondary" copyable>
            {item.phone}
          </Text>
        )}
        {formattedBirthDate && age !== null && (
          <Text type="secondary">
            {formattedBirthDate} ({age} ans)
          </Text>
        )}
        {item.locale && (
          <Text type="secondary">
            <TranslationOutlined /> {item.locale.toLocaleUpperCase()}
          </Text>
        )}
      </Flex>
    )
  }

  return (
    <>
      <ConditionalWrapper
        condition={!noLinkToDetails}
        wrapper={(children) => (
          <Link to={linkPath} aria-label={`Voir le profil de ${fullName}`}>
            {children}
          </Link>
        )}
      >
        <Flex align="center" gap={8} vertical={direction === 'vertical'}>
          {renderAvatar()}
          {renderName()}
          {renderAttributes()}
          {item.isActive === false && (
            <Tooltip title="Restricted Member">
              <InfoCircleOutlined
                style={{ color: 'red' }}
                aria-label="Restricted"
              />
            </Tooltip>
          )}
        </Flex>
      </ConditionalWrapper>

      {item.profilePictureUrl && pictureOpensInModal && (
        <Modal
          open={fullAvatarModalIsOpen}
          onCancel={handleModalClose}
          footer={null}
          centered
          width="auto"
        >
          <Flex align="center" justify="center">
            <Avatar
              size={350}
              src={item.profilePictureUrl}
              alt={`${fullName}'s picture`}
            />
          </Flex>
        </Modal>
      )}
    </>
  )
}

type MembershipProfileAvatarType = { Avatar: typeof MembershipProfileAvatar }

export { MembershipProfileAvatar, type MembershipProfileAvatarType }
